<script>
import { mapGetters } from 'vuex';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import ChatList from '../../../components/ChatList.vue';
// NOTICE: KAPTION-INTEGRATION - conversation-tabs.STRIP.1, FLAG.1-2
import KaptionConversationTabs from 'dashboard/components/kaption/KaptionConversationTabs.vue';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import { useKaptionDesktop } from 'dashboard/composables/useKaptionDesktop';
import ConversationBox from '../../../components/widgets/conversation/ConversationBox.vue';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import CmdBarConversationSnooze from 'dashboard/routes/dashboard/commands/CmdBarConversationSnooze.vue';
import CmdBarConversationMute from 'dashboard/routes/dashboard/commands/CmdBarConversationMute.vue';
import { emitter } from 'shared/helpers/mitt';
// NOTICE: KAPTION-INTEGRATION - usage-analytics.OPEN.1
import { trackUsage } from 'dashboard/composables/useUsageAnalytics';
import { USAGE_ANALYTICS_EVENTS } from 'dashboard/helper/usageAnalytics/constants';
import SidepanelSwitch from 'dashboard/components-next/Conversation/SidepanelSwitch.vue';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';
// NOTICE: KAPTION-INTEGRATION - contact-first-conversation.DRAFT.1
import KaptionDraftConversation from 'dashboard/components/kaption/KaptionContactFirst/KaptionDraftConversation.vue';

export default {
  components: {
    ChatList,
    ConversationBox,
    CmdBarConversationSnooze,
    CmdBarConversationMute,
    SidepanelSwitch,
    ConversationSidebar,
    // NOTICE: KAPTION-INTEGRATION - conversation-tabs.STRIP.1
    KaptionConversationTabs,
    // NOTICE: KAPTION-INTEGRATION - contact-first-conversation.DRAFT.1
    KaptionDraftConversation,
  },
  beforeRouteLeave(to, from, next) {
    // NOTICE: KAPTION-INTEGRATION - Only clear selected state if navigating AWAY from conversation view entirely
    // Don't clear when switching between conversations (to preserve iframe state)
    const toConversationId =
      to.params.conversation_id || to.params.conversationId;
    const isLeavingConversationView = !toConversationId;

    if (this.conversationId && isLeavingConversationView) {
      this.$store.dispatch('clearSelectedState');
    }
    next(); // Continue with navigation
  },
  props: {
    inboxId: {
      type: [String, Number],
      default: 0,
    },
    conversationId: {
      type: [String, Number],
      default: 0,
    },
    label: {
      type: String,
      default: '',
    },
    teamId: {
      type: [String, Number],
      default: '',
    },
    // NOTICE: KAPTION-INTEGRATION - Concrete agent filter from the full route path.
    assigneeId: {
      type: [String, Number],
      default: '',
    },
    conversationType: {
      type: String,
      default: '',
    },
    // NOTICE: KAPTION-INTEGRATION
    assistantId: {
      type: [String, Number],
      default: 0,
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
    // NOTICE: KAPTION-INTEGRATION - Sales Kanban route-driven view
    kanbanView: {
      type: Boolean,
      default: false,
    },
    // NOTICE: KAPTION-INTEGRATION - contact-first-conversation.DRAFT.1
    draftContactId: {
      type: [String, Number],
      default: 0,
    },
    draftInboxId: {
      type: [String, Number],
      default: 0,
    },
  },
  setup() {
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();
    // NOTICE: KAPTION-INTEGRATION - conversation-tabs.FLAG.1-2
    const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
    const { isDesktop } = useKaptionDesktop();

    return {
      uiSettings,
      updateUISettings,
      accountId,
      isKaptionFlagEnabled,
      isDesktop,
    };
  },
  data() {
    return {
      showSearchModal: false,
      // NOTICE: KAPTION-INTEGRATION - Track kanban view state (route-driven or toggle-driven)
      isKanbanActive: false,
    };
  },
  computed: {
    // NOTICE: KAPTION-INTEGRATION
    // conversation-tabs.FLAG.1-2 — the pilot account, and only inside Rumo
    // Desktop: in a browser the accelerators and the modifier-click belong to
    // the browser, so the strip would promise something it cannot keep.
    showConversationTabs() {
      return (
        this.isDesktop && this.isKaptionFlagEnabled('KAPTION_CONVERSATION_TABS')
      );
    },
    ...mapGetters({
      chatList: 'getAllConversations',
      currentChat: 'getSelectedChat',
    }),
    showConversationList() {
      // NOTICE: KAPTION-INTEGRATION - a draft behaves like an open conversation
      return this.isOnExpandedLayout
        ? !this.conversationId && !this.draftContactId
        : true;
    },
    showMessageView() {
      // NOTICE: KAPTION-INTEGRATION - Hide message view when kanban is active
      if (this.isKanbanActive) return false;
      // NOTICE: KAPTION-INTEGRATION - contact-first-conversation.DRAFT.1
      if (this.draftContactId) return true;
      return this.conversationId ? true : !this.isOnExpandedLayout;
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const { conversation_display_type: conversationDisplayType = CONDENSED } =
        this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },

    // NOTICE: KAPTION-INTEGRATION - Separate conditions for sidebar:
    // - shouldRenderSidebar: controls v-if (component exists in DOM)
    // - isSidebarVisible: controls v-show (visibility via CSS)
    // This ensures iframes stay alive when sidebar is closed
    shouldRenderSidebar() {
      // NOTICE: KAPTION-INTEGRATION - Hide sidebar when kanban is active
      if (this.isKanbanActive) return false;
      return !!this.currentChat.id;
    },
    isSidebarVisible() {
      // Controls visibility (CSS) - iframes stay alive when hidden
      const { is_contact_sidebar_open: isContactSidebarOpen } = this.uiSettings;
      // Default to true (open) if never explicitly set by user
      return isContactSidebarOpen ?? true;
    },
  },
  watch: {
    conversationId() {
      this.fetchConversationIfUnavailable();
    },
    // NOTICE: KAPTION-INTEGRATION - kanban state is driven solely by ChatList's
    // kanbanActive emit (which gates on the feature flag). We do NOT watch kanbanView
    // directly here — otherwise disabling the flag while on /kanban would leave
    // isKanbanActive true, hiding the message pane and sidebar (blank screen).
  },

  created() {
    // Clear selected state early if no conversation is selected
    // This prevents child components from accessing stale data
    // and resolves timing issues during navigation
    // with conversation view and other screens
    // NOTICE: KAPTION-INTEGRATION - Don't clear when entering kanban view
    if (!this.conversationId && !this.kanbanView) {
      this.$store.dispatch('clearSelectedState');
    }
  },

  mounted() {
    this.$store.dispatch('agents/get');
    this.$store.dispatch('portals/index');
    this.initialize();
    this.$watch('$store.state.route', () => this.initialize());
    this.$watch('chatList.length', () => {
      this.setActiveChat();
    });
  },

  methods: {
    onConversationLoad() {
      this.fetchConversationIfUnavailable();
    },
    initialize() {
      this.$store.dispatch('setActiveInbox', this.inboxId);
      this.setActiveChat();
    },
    toggleConversationLayout() {
      const { LAYOUT_TYPES } = wootConstants;
      const {
        conversation_display_type:
          conversationDisplayType = LAYOUT_TYPES.CONDENSED,
      } = this.uiSettings;
      const newViewType =
        conversationDisplayType === LAYOUT_TYPES.CONDENSED
          ? LAYOUT_TYPES.EXPANDED
          : LAYOUT_TYPES.CONDENSED;
      this.updateUISettings({
        conversation_display_type: newViewType,
        previously_used_conversation_display_type: newViewType,
      });
    },
    fetchConversationIfUnavailable() {
      if (!this.conversationId) {
        return;
      }
      const chat = this.findConversation();
      if (!chat) {
        this.$store.dispatch('getConversation', this.conversationId);
      }
    },
    findConversation() {
      const conversationId = parseInt(this.conversationId, 10);
      const [chat] = this.chatList.filter(c => c.id === conversationId);
      return chat;
    },
    setActiveChat() {
      if (this.conversationId) {
        const selectedConversation = this.findConversation();
        // If conversation doesn't exist or selected conversation is same as the active
        // conversation, don't set active conversation.
        if (
          !selectedConversation ||
          selectedConversation.id === this.currentChat.id
        ) {
          return;
        }
        const { messageId } = this.$route.query;
        // NOTICE: KAPTION-INTEGRATION - usage-analytics.OPEN.1: fires exactly
        // once per conversation entry (the guard above skips re-renders of the
        // same conversation). usage-analytics.OPEN.2: bounded, PII-free fields.
        trackUsage(USAGE_ANALYTICS_EVENTS.CONVERSATION_OPENED, {
          conversation_id: selectedConversation.id,
          inbox_id: selectedConversation.inbox_id,
          status: selectedConversation.status,
          assignee_type: selectedConversation.meta?.assignee ? 'agent' : 'none',
          unread: (selectedConversation.unread_count || 0) > 0,
        });
        this.$store
          .dispatch('setActiveChat', {
            data: selectedConversation,
            after: messageId,
          })
          .then(() => {
            emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
          });
      } else {
        this.$store.dispatch('clearSelectedState');
      }
    },
    onSearch() {
      this.showSearchModal = true;
    },
    closeSearch() {
      this.showSearchModal = false;
    },
    // NOTICE: KAPTION-INTEGRATION - Handle kanban active state from ChatList
    onKanbanActive(isActive) {
      this.isKanbanActive = isActive;
    },
  },
};
</script>

<template>
  <!-- NOTICE: KAPTION-INTEGRATION - Keep command-bar roots out of pane spacing -->
  <section class="wp-pane-row wp-conversation-split flex w-full h-full min-w-0">
    <ChatList
      :show-conversation-list="showConversationList || isKanbanActive"
      :conversation-inbox="inboxId"
      :label="label"
      :team-id="teamId"
      :assignee-id="assigneeId"
      :conversation-type="conversationType"
      :folders-id="foldersId"
      :assistant-id="assistantId"
      :is-on-expanded-layout="isOnExpandedLayout || isKanbanActive"
      :kanban-view="kanbanView"
      @conversation-load="onConversationLoad"
      @kanban-active="onKanbanActive"
    />
    <!-- NOTICE: KAPTION-INTEGRATION
         conversation-tabs.STRIP.1 — the tabs belong to the conversation and
         its sidebar, so they span those two and leave the list alone. The
         inner row keeps the split's own classes so the 8px between the
         conversation card and the contact card still comes from the same
         rule; the column takes that gap from the list itself. -->
    <div
      v-if="showMessageView"
      class="flex flex-col flex-1 min-w-0 min-h-0 ltr:ml-2 rtl:mr-2"
    >
      <KaptionConversationTabs
        v-if="showConversationTabs && currentChat.id"
        :account-id="accountId"
      />
      <div
        class="wp-pane-row wp-conversation-split flex flex-1 min-h-0 min-w-0"
      >
        <!-- NOTICE: KAPTION-INTEGRATION - contact-first-conversation.DRAFT.1 -->
        <KaptionDraftConversation
          v-if="draftContactId && draftInboxId"
          :key="`${draftContactId}-${draftInboxId}`"
          :contact-id="draftContactId"
          :inbox-id="draftInboxId"
        />
        <ConversationBox
          v-else
          :inbox-id="inboxId"
          :is-on-expanded-layout="isOnExpandedLayout"
        >
          <SidepanelSwitch v-if="currentChat.id" />
        </ConversationBox>
        <!-- NOTICE: KAPTION-INTEGRATION - v-if keeps component when chat selected, v-show hides but keeps iframes alive -->
        <ConversationSidebar
          v-if="shouldRenderSidebar"
          v-show="isSidebarVisible"
          :current-chat="currentChat"
        />
      </div>
    </div>
    <CmdBarConversationSnooze />
    <CmdBarConversationMute />
  </section>
</template>
